import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  PlanType,
  USER_STATUS,
  projectDuration,
  projectName,
} from '../constants';
import { Plan, Plans } from '../deprecated/payment/types';
import Product from 'src/models/Product';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  setAllPlansSorted,
  setSignupStep,
  setSelectedPlanId,
  setAvailablePlansWithoutDefaultCheck,
  setIsFitherProjectUser,
  setChallengePlan,
  setIsChallengePackageFlow,
  setIsChallengeBundleFlow,
  setIsBypassPlanSelection,
  setIsNutritionCoachingAdded,
} from 'src/store/signup';

import {
  getEcommerceDataProperties,
  getRecommendedSubscriptionSignup,
  isFreeTrialURL,
  isUserFromAustralia,
  isUserFromNewZealand,
  isUserFromUK,
  returnFilteredPlans,
} from 'src/lib/utils';
import {
  EVENTS,
  track,
  trackSignupPlanSelectionViewed,
  trackSignupCheckoutStarted,
} from 'src/util/track';
import { useChallengeSelectionPreview } from './hooks/useChallengeSelectionPreview';
import moment from 'moment';
import { Layout } from 'src/components/layout-wrapper/Layout';
import Loader from 'src/components/content/loader';
import { optimizely } from 'src/setupOptimizely';
import { useDecision } from '@optimizely/react-sdk';
import {
  checkQuizStatus,
  trackCheckoutService,
} from 'src/services/quiz-service';
import { useHistory } from 'react-router-dom';
import { updateQuizData } from 'src/store/quiz';
import User from 'src/models/User';
import { useSelector } from 'react-redux';
import { IAppState } from 'src/store';
import { stripeProductsApi } from 'src/services';

import ChallengePackageFlow from './module/ChallengePackageFlow';
import ChallengeBundleFlow from './module/ChallengeBundleFlow';
import { ChallengeBundleParamId, ChallengePlanId } from '../types';
import {
  extractPlans,
  findChallengePlan,
  getDefaultPlanByCountry,
  mergeChallengePlans,
  sortSubscriptionPlans,
} from './utils/plans.util';
import { useChallengeProducts } from './hooks/useChallengeProducts';
import StandardPlanFlowNew from './module/StandardPlanFlowNew';
import { useShopifyVariants } from 'src/hooks/useShopifyVariants';
import {
  CHALLENGE_BUNDLE_QUERY_PARAM,
  getChallengeBundlePlans,
} from './utils/challengeBundle.util';
import { useScrollToTop } from 'src/hooks/useScrollToTop';
import { getCoachingAddonFromSession } from 'src/util/addon-storage.util';

export interface SubscriptionPageProps {
  planType: PlanType;
  setPlanType: (planType: PlanType) => void;
  addonWeek: number;
  setAddonWeek: (value: number) => void;
  isStandardPlan: boolean;
  onCheckoutClick: () => void;
  availablePlans: Array<Plan>;
  selectedPlanId: string;
  showTwoStepProcess: boolean;
  country: string | null;
  isTwelveMonthParam?: boolean;
  showClientTestimonials?: boolean;
  hideOtherDurations?: boolean;
}

const SubscriptionPage = () => {
  const history = useHistory();
  const userEmail = useSelector((state: IAppState) => state.auth?.user?.email);

  useScrollToTop();

  // url params
  const searchParams = new URLSearchParams(window.location.search);

  // typeform quiz params
  const scale = searchParams.get('scale');
  const challengeParam = searchParams.get('challenge');
  const feeling = searchParams.get('feeling');
  const support = searchParams.get('support');
  const country = searchParams.get('country');
  const isFromWebsite = searchParams.get('website');
  const challengePackageParam = searchParams.get(
    'challengePackage'
  ) as ChallengePlanId | null;
  const challengeBundleParam = searchParams.get(
    CHALLENGE_BUNDLE_QUERY_PARAM
  ) as ChallengeBundleParamId | null;

  const firstName = searchParams.get('firstName');
  const showClientTestimonials = !firstName;

  // bypass plan selection param — when true, auto-select plan and advance to next step
  const isBypassPlanSelection =
    searchParams.get('bypassPlanSelection') === 'true';

  const {
    isLoading: isChallengeProductsLoading,
    showChallengeProducts,
    challengeStartDate,
    challengeData,
  } = useChallengeProducts();

  const showChallengeBundleFlow = Boolean(challengeBundleParam);
  const showChallengePackageFlow =
    showChallengeBundleFlow || Boolean(challengePackageParam);

  // If challenge package or bundle param exists but challenge products are not available, remove param and reload
  useEffect(() => {
    if (!showChallengeProducts && !isChallengeProductsLoading) {
      const newSearchParams = new URLSearchParams(window.location.search);
      let shouldReload = false;
      if (challengePackageParam) {
        newSearchParams.delete('challengePackage');
        shouldReload = true;
      }
      if (challengeBundleParam) {
        newSearchParams.delete(CHALLENGE_BUNDLE_QUERY_PARAM);
        shouldReload = true;
      }
      if (shouldReload) {
        window.location.search = newSearchParams.toString();
      }
    }
  }, [
    challengePackageParam,
    challengeBundleParam,
    isChallengeProductsLoading,
    showChallengeProducts,
  ]);

  const [decision] = useDecision('website_programs_bypass_quiz', {
    autoUpdate: true,
  });
  const isNewZealandUser = isUserFromNewZealand() || country === 'New Zealand';

  // plan params
  const planTypeParam = searchParams.get('planType')?.toLowerCase() as PlanType;
  const durationParam = searchParams.get('duration') ?? '3';
  const hideOtherDurations = searchParams.get('showall') === 'false';

  const isTwelveMonthParam = Number(durationParam) === 12;

  // qr code project params
  const projectNameParam = searchParams.get('project')?.toLowerCase();

  //email check
  const emailParam = searchParams.get('email');

  // check validity for FitHer project
  const isValidForFitherProject = useMemo(() => {
    const validProjectName = projectNameParam === projectName;
    const validDuration = moment().isBetween(
      projectDuration.start,
      projectDuration.end
    );

    return validProjectName && validDuration;
  }, [projectNameParam]);

  const [planType, setPlanType] = useState<PlanType>(
    planTypeParam || 'standard'
  );
  const [sortedPlans, setSortedPlans] = useState<Plan[]>([]);
  const [addonWeek, setAddonWeek] = useState(0);
  const [isInitialRender, toggleIsInitialRender] = useState(true);

  const [showStandardPlan, setShowStandardPlan] = useState(true);
  // Fetch shopify variants into Redux (used by child components)
  useShopifyVariants();

  const [loading, setIsLoading] = useState(false);

  const {
    availablePlans,
    selectedPlanId,
    allPlansSorted,
    addonWeek: mealAddonWeek,
    challengePackageSelectedPlan,
  } = useAppSelector(state => state.signUp);

  const user = useAppSelector(state => state.auth?.user);
  const isAuthenticated = useAppSelector(state => state.auth?.isAuthenticated);
  const dispatch = useAppDispatch();

  // Sync bypassPlanSelection param to Redux once on mount
  useEffect(() => {
    dispatch(setIsBypassPlanSelection(isBypassPlanSelection));
  }, [dispatch, isBypassPlanSelection]);

  // Restore coaching addon selection if previously selected in session storage
  useEffect(() => {
    if (getCoachingAddonFromSession()) {
      dispatch(setIsNutritionCoachingAdded(true));
    }
  }, [dispatch]);

  const mergedChallengePlans = mergeChallengePlans(
    allPlansSorted,
    challengeStartDate,
    challengeData?.asset_metadata
  );
  const challengeBundlePlans = useMemo(
    () =>
      getChallengeBundlePlans(allPlansSorted, challengeData?.asset_metadata),
    [allPlansSorted, challengeData?.asset_metadata]
  );

  const isStandardPlan = planType === 'standard';
  const showTwoStepProcess =
    user?.status === USER_STATUS.PROSPECT ||
    user?.status === USER_STATUS.INACTIVE;
  const signupVariant = showTwoStepProcess ? 'two_step' : 'three_step';
  const funnelPlanType = showChallengeBundleFlow
    ? 'challenge_bundle'
    : showChallengePackageFlow
    ? 'challenge_package'
    : 'standard';
  const hasTrackedPlanSelectionViewRef = useRef(false);

  useEffect(() => {
    if (hasTrackedPlanSelectionViewRef.current) {
      return;
    }

    if (
      !selectedPlanId &&
      !allPlansSorted.length &&
      !showChallengePackageFlow
    ) {
      return;
    }

    trackSignupPlanSelectionViewed(
      signupVariant,
      funnelPlanType,
      selectedPlanId || undefined,
      country || undefined
    );
    hasTrackedPlanSelectionViewRef.current = true;
  }, [
    allPlansSorted.length,
    country,
    selectedPlanId,
    showChallengePackageFlow,
    signupVariant,
    funnelPlanType,
  ]);

  const onCheckoutClick = async () => {
    trackSignupCheckoutStarted(
      funnelPlanType,
      selectedPlanId,
      showTwoStepProcess ? 'payment' : 'account'
    );
    track(EVENTS.subscriptionPlan, {
      isStandardPlanSelected: isStandardPlan,
      selectedPlan: selectedPlanId,
      mealAddonWeek,
      bypassPlanSelection: isBypassPlanSelection,
    });
    track(EVENTS.beginCheckout, { ecommerce: null });
    track(EVENTS.beginCheckout, {
      logged_in_status: Boolean(user),
      bypassPlanSelection: isBypassPlanSelection,
      ecommerce: getEcommerceDataProperties(
        selectedPlanId,
        availablePlans,
        {},
        1,
        user
      ),
    });
    if (emailParam || userEmail) {
      const emailToSend = emailParam ?? userEmail ?? '';

      if (emailToSend) {
        await trackCheckoutService({ email: emailToSend });
      }
    }
    if (showTwoStepProcess) {
      dispatch(setSignupStep(3));
      return;
    }
    dispatch(setSignupStep(2));
  };

  const sortPlans = useCallback(
    (plans: Plans) => {
      const sortedPlansArr = sortSubscriptionPlans(plans);
      setSortedPlans(sortedPlansArr);
      dispatch(setAllPlansSorted(sortedPlansArr));
    },
    [dispatch]
  );

  const handleRedirectionCondition = useCallback(
    (user: User | undefined) => {
      const redirectTo = '/account';

      const allowedStatus = [
        USER_STATUS.ACTIVE,
        USER_STATUS.INITIAL,
        USER_STATUS.FINAL,
      ];

      if (allowedStatus.includes(user?.status ?? '')) {
        history.push(redirectTo);
      }
    },
    [history]
  );

  // set plan and navigate to step 2 if fither project user
  useEffect(() => {
    const plan = allPlansSorted.find(
      plan =>
        plan.id.includes('Standard') &&
        plan.interval === 'month' &&
        plan.intervalCount === 1
    );

    track(EVENTS.fitHerProjectUser);

    if (isValidForFitherProject && plan) {
      dispatch(setIsFitherProjectUser(true));

      setTimeout(() => {
        dispatch(setSelectedPlanId(plan.id));
        dispatch(setSignupStep(2));
      }, 700);
    }
  }, [allPlansSorted, dispatch, isValidForFitherProject]);

  useEffect(() => {
    dispatch(setAllPlansSorted([]));
  }, [dispatch]);

  // Get Product Data on Mount
  useEffect(() => {
    let mounted = true;
    (async (): Promise<void> => {
      if (!allPlansSorted.length) {
        try {
          setIsLoading(true);
          // Get Products Data

          const response = await stripeProductsApi({
            isChallengePackage: showChallengePackageFlow,
            currency: isUserFromUK() ? 'gbp' : undefined,
          });
          const productsData = response.data.products;

          if (!mounted) return;

          const tempPlans: Plans = {};
          // Find Product
          Object.values(productsData).forEach((productEntry: unknown) => {
            const tempProduct: Product = productEntry as Product;
            if (
              tempProduct?.metadata?.EQPlanID === '1' ||
              tempProduct?.metadata?.EQPlanID === '4' ||
              tempProduct?.metadata?.challenge ||
              tempProduct?.metadata?.isSignupAddon === 'true'
            ) {
              extractPlans(tempPlans, tempProduct);
            }
          });

          sortPlans(tempPlans);
        } catch (e) {
          console.log('getProducts error', e);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSortedPlans([...allPlansSorted]);
      }
    })();
    return (): void => {
      mounted = false;
    };
  }, [sortPlans, allPlansSorted, showChallengePackageFlow]);

  useEffect(() => {
    if (isInitialRender && sortedPlans.length) {
      const filteredPlans = returnFilteredPlans(sortedPlans);
      dispatch(
        setAvailablePlansWithoutDefaultCheck([...filteredPlans].reverse())
      );
    }
  }, [
    sortedPlans,
    planType,
    dispatch,
    isInitialRender,
    isValidForFitherProject,
  ]);

  useEffect(() => {
    if (showChallengePackageFlow) {
      return;
    }

    // Always use Standard plan - Premium plans are no longer available
    if (isInitialRender && availablePlans.length) {
      let isDefaultPlanSelected = false;

      // Set Standard as default plan type
      setPlanType('standard');
      setShowStandardPlan(true);

      //logic to handle query params for quiz logic and select subscription
      if (!!support && !!scale && !!challengeParam && !!feeling && !!country) {
        track(EVENTS.typeformSubmit);
        track(EVENTS.quizComplete, { quiz: 'typeform' });

        const recommendedSubscription = getRecommendedSubscriptionSignup(
          scale,
          challengeParam,
          feeling,
          support,
          country,
          allPlansSorted
        );

        if (recommendedSubscription) {
          dispatch(setSelectedPlanId(recommendedSubscription));
          isDefaultPlanSelected = true;
        }
      }
      if (planTypeParam && durationParam) {
        const duration = parseInt(durationParam);

        const plan = availablePlans.find(
          plan => plan.interval === 'month' && plan.intervalCount === duration
        );

        if (plan?.id) {
          dispatch(setSelectedPlanId(plan.id));
          isDefaultPlanSelected = true;
        }
      }

      if (!isDefaultPlanSelected) {
        const { planId } = getDefaultPlanByCountry(allPlansSorted);

        if (planId) {
          dispatch(setSelectedPlanId(planId));
        }
      }

      setTimeout(() => {
        toggleIsInitialRender(false);
      }, 500);
    }
  }, [
    allPlansSorted,
    dispatch,
    durationParam,
    planTypeParam,
    showStandardPlan,
    isInitialRender,
    challengeParam,
    country,
    feeling,
    scale,
    selectedPlanId,
    support,
    availablePlans,
    isNewZealandUser,
    showChallengePackageFlow,
  ]);

  useEffect(() => {
    if (isFromWebsite) {
      if (decision?.variationKey === 'off') {
        history.replace('/quiz');
      }
    } else {
      optimizely.track('completed_quiz'); // event to track for quiz flow conversion
    }
  }, [decision?.variationKey, history, isFromWebsite]);

  useEffect(() => {
    if (
      !showChallengePackageFlow ||
      !allPlansSorted.length ||
      isChallengeProductsLoading
    ) {
      return;
    }

    if (!isInitialRender && challengePackageSelectedPlan && selectedPlanId) {
      return;
    }

    if (showChallengeBundleFlow) {
      const matchedPlan = findChallengePlan(
        challengeBundlePlans,
        challengeBundleParam
      );
      const selectedPlan = matchedPlan ?? challengeBundlePlans[0] ?? null;

      if (selectedPlan) {
        dispatch(setChallengePlan(selectedPlan));
        dispatch(
          setSelectedPlanId(selectedPlan.checkoutPlanId ?? selectedPlan.id)
        );
      }

      if (isInitialRender) {
        setTimeout(() => {
          toggleIsInitialRender(false);
        }, 500);
      }
      return;
    }

    // Challenge Package Flow
    let matchedPlan = findChallengePlan(
      mergedChallengePlans,
      challengePackageParam
    );

    // Non-Australian users cannot select supplements plan
    if (
      !isUserFromAustralia() &&
      matchedPlan?.localId === ChallengePlanId.CHALLENGE_WITH_SUPPLEMENTS
    ) {
      matchedPlan = null;
    }

    const defaultPackagePlan = !isUserFromAustralia()
      ? mergedChallengePlans.find(
          p => p.localId === ChallengePlanId.ONLY_CHALLENGE
        ) ?? mergedChallengePlans[0]
      : mergedChallengePlans.find(
          p => p.localId === ChallengePlanId.CHALLENGE_WITH_SUPPLEMENTS
        ) ??
        mergedChallengePlans.find(
          p => p.localId === ChallengePlanId.ONLY_CHALLENGE
        ) ??
        mergedChallengePlans[0];

    const selectedPlan = matchedPlan ?? defaultPackagePlan ?? null;

    if (selectedPlan) {
      dispatch(setChallengePlan(selectedPlan));
      dispatch(
        setSelectedPlanId(selectedPlan.checkoutPlanId ?? selectedPlan.id)
      );
    }

    if (isInitialRender) {
      setTimeout(() => {
        toggleIsInitialRender(false);
      }, 500);
    }
  }, [
    allPlansSorted.length,
    challengeBundleParam,
    challengeBundlePlans,
    challengePackageParam,
    challengePackageSelectedPlan,
    dispatch,
    isChallengeProductsLoading,
    isInitialRender,
    mergedChallengePlans,
    selectedPlanId,
    showChallengeBundleFlow,
    showChallengePackageFlow,
  ]);

  useEffect(() => {
    dispatch(setIsChallengePackageFlow(showChallengePackageFlow));
  }, [showChallengePackageFlow, dispatch]);

  useEffect(() => {
    dispatch(setIsChallengeBundleFlow(showChallengeBundleFlow));
  }, [showChallengeBundleFlow, dispatch]);

  // When bypassPlanSelection=true, call onCheckoutClick once the plan is resolved.
  // onCheckoutClick fires all existing tracking events and navigates to the correct
  // next step based on showTwoStepProcess:
  //   PROSPECT/INACTIVE → step 3 (Payment)
  //   new user          → step 2 (CreateAccount)
  useEffect(() => {
    if (!isBypassPlanSelection || isInitialRender || !selectedPlanId) return;
    onCheckoutClick();
    // onCheckoutClick is intentionally omitted — it is recreated each render but this
    // effect is gated on isInitialRender flipping once, so it fires exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBypassPlanSelection, isInitialRender, selectedPlanId]);

  useChallengeSelectionPreview(showChallengePackageFlow);

  useEffect(() => {
    if (isAuthenticated && user) {
      handleRedirectionCondition(user);
    }
  }, [handleRedirectionCondition, isAuthenticated, user]);

  const checkEmailQuizStatus = useCallback(
    async (email: string, user: User | null) => {
      const response = await checkQuizStatus({ email });
      if (response?.data) {
        if (
          user &&
          user.status === USER_STATUS.PROSPECT &&
          !response.data.isQuizExpired
        ) {
          dispatch(updateQuizData({ isQuiz: true }));
        } else if (response.data.isQuizExpired) {
          if (!isFreeTrialURL() && !showChallengePackageFlow) {
            history.replace('/quiz?email=' + encodeURIComponent(email));
          }
        } else if (response.data.data) {
          dispatch(updateQuizData({ isQuiz: true }));
        }
      }
    },
    [dispatch, history, showChallengePackageFlow]
  );

  useEffect(() => {
    if (!scale) {
      // if scale is set, meaning user rendered from type form, so ignore checking quiz status
      if (emailParam) {
        checkEmailQuizStatus(emailParam, null);
      } else if (user?.email) {
        checkEmailQuizStatus(user.email, user);
      }
    }
  }, [checkEmailQuizStatus, emailParam, user, scale]);

  const props: SubscriptionPageProps = {
    planType,
    setPlanType,
    addonWeek,
    setAddonWeek,
    isStandardPlan,
    onCheckoutClick,
    availablePlans,
    selectedPlanId,
    showTwoStepProcess,
    country,
    hideOtherDurations,
  };

  if (
    isValidForFitherProject ||
    loading ||
    isChallengeProductsLoading ||
    isBypassPlanSelection ||
    !allPlansSorted.length ||
    isInitialRender
  ) {
    return (
      <Layout module="login">
        <Loader />
      </Layout>
    );
  }

  if (showChallengePackageFlow) {
    if (showChallengeBundleFlow) {
      return (
        <ChallengeBundleFlow {...props} challengePlans={challengeBundlePlans} />
      );
    }

    return <ChallengePackageFlow {...props} />;
  }

  return (
    <StandardPlanFlowNew
      {...props}
      selectedPlanId={selectedPlanId}
      availablePlans={availablePlans}
      setPlanType={setPlanType}
      isTwelveMonthParam={isTwelveMonthParam}
      showClientTestimonials={showClientTestimonials}
    />
  );
};

export default SubscriptionPage;
